import { el, clear, fmtInt, fmtDate, relativeTime } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import { getOrganizationDetail } from '../api/monitoring';
import { setOrganizationVenueLimit } from '../api/venue-requests';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import type {
  OrganizationDetail,
  MemberRow,
  DeviceRow,
  VenueRow,
  AuditRow,
} from '../api/types';
import { billingCard, tonePillClass } from './teams-billing-format';
import type { BillingBlock } from './teams-billing-format';

export async function renderOrgDetail({ root, params }: RouteContext) {
  const content = renderShell(root, 'teams');
  const orgId = params.orgId;
  let windowDays = 30;

  const backLink = el(
    'button',
    { class: 'btn btn-ghost back-link', onClick: () => navigate('/teams') },
    ['‹ All teams'],
  );
  const body = el('div', { class: 'detail-body' });
  content.append(backLink, body);

  async function load() {
    renderLoading(body, 'Loading team…');
    try {
      const data = await getOrganizationDetail(orgId, { windowDays });
      renderDetail(body, orgId, data, windowDays, (next) => {
        windowDays = next;
        void load();
      });
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(body, err instanceof Error ? err.message : 'Failed to load team.');
    }
  }

  await load();
}

function renderDetail(
  container: HTMLElement,
  orgId: string,
  data: OrganizationDetail,
  windowDays: number,
  onWindowChange: (days: number) => void,
): void {
  clear(container);
  const org = data.organization;

  const statusCls = org.suspendedAt
    ? 'pill pill-danger'
    : org.subscriptionStatus === 'ACTIVE'
      ? 'pill pill-ok'
      : 'pill pill-warn';

  const header = el('div', { class: 'detail-head' }, [
    el('div', {}, [
      el('h1', { textContent: org.name }),
      el('div', { class: 'cell-muted', textContent: org.slug }),
    ]),
    el('div', { class: 'detail-head-pills' }, [
      el('span', { class: 'pill', textContent: org.subscriptionTier }),
      el('span', {
        class: statusCls,
        textContent: org.suspendedAt ? 'SUSPENDED' : org.subscriptionStatus,
      }),
    ]),
  ]);

  const meta = el('div', { class: 'meta-row' }, [
    metaItem('Billing', org.billingProvider ?? '—'),
    metaItem('Passback window', `${org.passbackWindowHours}h`),
    metaItem('Retention', `${org.dataRetentionDays}d`),
    metaItem('Created', fmtDate(org.createdAt)),
  ]);

  const windowSelect = el('select', { class: 'control' });
  for (const [v, label] of [
    ['7', 'Last 7 days'],
    ['30', 'Last 30 days'],
    ['90', 'Last 90 days'],
  ] as [string, string][]) {
    const opt = el('option', { value: v, textContent: label });
    if (Number(v) === windowDays) opt.selected = true;
    windowSelect.append(opt);
  }
  windowSelect.addEventListener('change', () =>
    onWindowChange(Number(windowSelect.value)),
  );

  const s = data.scanSummary;
  const cards = el('div', { class: 'cards' }, [
    statCard('Scans', fmtInt(s.total)),
    statCard('Accepted', fmtInt(s.accepted)),
    statCard('Rejected', fmtInt(s.rejected), s.rejected > 0 ? 'warn' : ''),
    statCard('Undetermined', fmtInt(s.undetermined)),
    statCard('Passback hits', fmtInt(s.passbackHits), s.passbackHits > 0 ? 'flag' : ''),
    statCard('Last scan', relativeTime(s.lastScanAt)),
  ]);

  container.append(header, meta);
  // Skipped when the API sent no `billing` (it predates the field).
  const billing = billingSection(data);
  if (billing) container.append(billing);
  container.append(
    el('div', { class: 'section-head' }, [
      el('h2', { textContent: 'Activity' }),
      windowSelect,
    ]),
    cards,
    venueLimitSection(
      orgId,
      org.name,
      org.subscriptionTier,
      org.billingProvider,
      data.venues.length,
      org.maxVenues,
      data.venueCapacity,
    ),
    section('Venues', venuesTable(data.venues)),
    section('Members', membersTable(data.members)),
    section('Devices', devicesTable(data.devices)),
    section('Recent audit log', auditTable(data.auditLog)),
  );
}

/**
 * One block per Teams subscription (Apple or Stripe), newest first as the API
 * sends them: a head with the rail, plan and state, then the details that rail
 * could give. Everything that came from a customer (the cancel comment, the
 * email) is set as text.
 */
function billingSection(data: OrganizationDetail): HTMLElement | null {
  const card = billingCard(data.billing);
  if (!card) return null;
  const host = el('div', {});
  if (card.kind === 'empty') {
    host.append(el('div', { class: 'empty' }, [card.text]));
  } else {
    host.append(...card.blocks.map(billingBlock));
  }
  return section('Billing', host);
}

function billingBlock(block: BillingBlock): HTMLElement {
  const head = el('div', { class: 'billing-head' }, [
    el('span', { class: 'cell-strong', textContent: block.rail }),
    el('span', { class: 'cell-muted', textContent: block.plan }),
    block.sandbox ? el('span', { class: 'pill', textContent: 'Sandbox' }) : null,
    el('span', {
      class: `${tonePillClass(block.state.tone)} billing-state`,
      textContent: block.state.text,
      title: block.state.title ?? '',
    }),
  ]);
  return el('div', { class: 'billing-block' }, [
    head,
    block.rows.length > 0
      ? el(
          'div',
          { class: 'meta-row' },
          block.rows.map((row) => metaItem(row.label, row.value, row.title)),
        )
      : null,
  ]);
}

function metaItem(label: string, value: string, title?: string): HTMLElement {
  return el('div', { class: 'meta-item' }, [
    el('div', { class: 'meta-label', textContent: label }),
    el('div', { class: 'meta-value', textContent: value, title: title ?? '' }),
  ]);
}

function statCard(label: string, value: string, tone = ''): HTMLElement {
  return el('div', { class: `card ${tone ? `card-${tone}` : ''}` }, [
    el('div', { class: 'card-value', textContent: value }),
    el('div', { class: 'card-label', textContent: label }),
  ]);
}

function section(title: string, table: HTMLElement): HTMLElement {
  return el('section', { class: 'detail-section' }, [
    el('h2', { textContent: title }),
    table,
  ]);
}

/**
 * 3.0 sells one venue per org by default; going over that takes a manual
 * override here (or the Stripe multilocation resolver, once web purchase is
 * back on). `maxVenues: null` (or, until the admin endpoint returns the
 * field, `undefined`) means "no override — plan default" rather than "no
 * limit", so the copy never claims a specific default number it can't be
 * sure of across tiers.
 *
 * `venueCapacity` is what PlanLimitsService.assertCanAddVenues enforces —
 * ACTIVE-licensed venues and the effective limit — so the section shows the
 * numbers the server will actually hold an owner to. Only an older API that
 * doesn't send it falls back to "Venues on file" (every row, archived ones
 * included), with a caption saying that count isn't the enforced one.
 *
 * A Stripe-billed org's Organization.maxVenues is overwritten by the
 * subscription's item quantity on every billing resolution
 * (BillingResolverService.write) — see PlanLimitsService
 * .setVenueLimitOverride. A value set here on one survives only until the
 * next sync, so the copy calls it out instead of labelling it "override".
 */
function venueLimitSection(
  orgId: string,
  orgName: string,
  tier: string,
  billingProvider: string | null,
  venuesOnFile: number,
  initialMaxVenues: number | null | undefined,
  capacity: { current: number; limit: number | null } | undefined,
): HTMLElement {
  const host = el('div', {});
  let maxVenues = initialMaxVenues ?? null;
  const isStripe = billingProvider === 'STRIPE';
  // Mirrors TIER_LIMITS in the API's plan-limits.service.ts: every
  // self-serve tier defaults to 1 venue; only Enterprise (billed off-app)
  // defaults to unlimited. Kept in sync by hand, same as api/types.ts.
  const tierDefaultLabel =
    tier === 'ENTERPRISE' ? 'unlimited by default (Enterprise)' : '1 by default for this plan';

  function render(message?: { text: string; tone: 'ok' | 'error' }): void {
    clear(host);

    const limitValue =
      maxVenues === null
        ? `Plan default — ${tierDefaultLabel}`
        : isStripe
          ? `${maxVenues} (set by Stripe subscription quantity)`
          : `${maxVenues} (override)`;

    // The enforced numbers, while they still describe the saved limit: after a
    // save or clear here, capacity.limit is from before it, so the effective
    // limit is shown from the new value until the page reloads.
    const enforcedLimit =
      capacity && maxVenues === (initialMaxVenues ?? null) ? capacity.limit : undefined;
    const meta = el('div', { class: 'meta-row' }, [
      capacity
        ? metaItem(
            'Active venues',
            enforcedLimit === undefined
              ? String(capacity.current)
              : `${capacity.current} of ${enforcedLimit === null ? 'unlimited' : enforcedLimit}`,
          )
        : metaItem('Venues on file', String(venuesOnFile)),
      metaItem('Limit', limitValue),
    ]);
    const captionEl = el('p', { class: 'view-caption', style: 'margin:4px 0 12px' }, [
      capacity
        ? 'Counts active, licensed venues — the ones the limit is enforced against.'
        : '“Venues on file” includes inactive ones — the limit is enforced against active, licensed venues only.',
    ]);

    const input = el('input', {
      class: 'control',
      type: 'number',
      min: '1',
      max: '500',
      step: '1',
      placeholder: 'Plan default',
      value: maxVenues !== null ? String(maxVenues) : '',
    });
    const saveBtn = el('button', { class: 'btn btn-primary btn-sm', type: 'button' }, [
      'Save override',
    ]);
    const clearBtn = el(
      'button',
      { class: 'btn btn-ghost btn-sm', type: 'button', disabled: maxVenues === null },
      ['Clear override'],
    );
    const formRow = el('div', { class: 'button-row', style: 'justify-content:flex-start' }, [
      input,
      saveBtn,
      clearBtn,
    ]);

    const statusHost = el('div', {});
    if (message) {
      statusHost.append(
        el(
          'div',
          {
            class: message.tone === 'error' ? 'banner banner-error' : 'banner',
            style: message.tone === 'ok' ? 'background:var(--ok-bg);color:var(--ok-fg)' : '',
            role: 'alert',
          },
          [message.text],
        ),
      );
    }

    saveBtn.addEventListener('click', async () => {
      const raw = input.value.trim();
      const n = Number(raw);
      if (!raw || !Number.isInteger(n) || n < 1 || n > 500) {
        render({ text: 'Enter a whole number of venues between 1 and 500.', tone: 'error' });
        return;
      }
      const fromLabel = maxVenues !== null ? String(maxVenues) : 'the plan default';
      const warnings: string[] = [];
      if (n < venuesOnFile) {
        warnings.push(
          `${orgName} has ${venuesOnFile} venues on file — some may be active, and this could block them.`,
        );
      }
      if (isStripe) {
        warnings.push(
          `${orgName} is billed via Stripe — the subscription quantity will likely overwrite this at the next billing sync.`,
        );
      } else if (tier === 'ENTERPRISE' && maxVenues === null) {
        warnings.push(
          `${orgName} is Enterprise, unlimited by default — this will cap it at ${n}.`,
        );
      }
      const question = [`Set ${orgName}’s venue limit from ${fromLabel} to ${n}?`, ...warnings].join(
        '\n\n',
      );
      if (!window.confirm(question)) return;
      saveBtn.disabled = true;
      clearBtn.disabled = true;
      try {
        await setOrganizationVenueLimit(orgId, n);
        maxVenues = n;
        render({
          text: isStripe
            ? `Venue limit set to ${n}. Stripe’s next billing sync may replace this with the subscription quantity.`
            : `Venue limit set to ${n}.`,
          tone: 'ok',
        });
      } catch (err) {
        if (err instanceof UnauthorizedError) return navigate('/login');
        render({
          text: err instanceof Error ? err.message : 'Failed to set the venue limit.',
          tone: 'error',
        });
      }
    });

    clearBtn.addEventListener('click', async () => {
      const question = isStripe
        ? `Clear ${orgName}’s venue limit? It’s billed via Stripe, so the next billing sync will likely restore ${maxVenues} anyway — the subscription quantity is what actually controls this.`
        : `Clear ${orgName}’s venue limit override and use the plan default?`;
      if (!window.confirm(question)) return;
      saveBtn.disabled = true;
      clearBtn.disabled = true;
      try {
        await setOrganizationVenueLimit(orgId, null);
        maxVenues = null;
        render({
          text: isStripe
            ? 'Venue limit reset to the plan default. Stripe’s next billing sync will likely set it back to the subscription quantity.'
            : 'Venue limit reset to the plan default.',
          tone: 'ok',
        });
      } catch (err) {
        if (err instanceof UnauthorizedError) return navigate('/login');
        render({
          text: err instanceof Error ? err.message : 'Failed to reset the venue limit.',
          tone: 'error',
        });
      }
    });

    host.append(meta, captionEl, formRow, statusHost);
  }

  render();
  return section('Venue limit', host);
}

function table(headers: string[], rows: HTMLElement[]): HTMLElement {
  if (rows.length === 0) {
    return el('div', { class: 'empty' }, ['None.']);
  }
  return el('div', { class: 'table-wrap' }, [
    el('table', { class: 'data-table' }, [
      el('thead', {}, [el('tr', {}, headers.map((h) => el('th', { textContent: h })))]),
      el('tbody', {}, rows),
    ]),
  ]);
}

function venuesTable(venues: VenueRow[]): HTMLElement {
  return table(
    ['Name', 'Address', 'Timezone', 'Active', 'Created'],
    venues.map((v) =>
      el('tr', {}, [
        el('td', { class: 'cell-strong', textContent: v.name }),
        el('td', { textContent: v.address ?? '—' }),
        el('td', { class: 'cell-muted', textContent: v.timezone }),
        el('td', {}, [
          el('span', {
            class: v.isActive ? 'pill pill-ok' : 'pill pill-warn',
            textContent: v.isActive ? 'Active' : 'Inactive',
          }),
        ]),
        el('td', { class: 'cell-muted', textContent: fmtDate(v.createdAt) }),
      ]),
    ),
  );
}

function membersTable(members: MemberRow[]): HTMLElement {
  return table(
    ['User', 'Role', 'Venue', 'Status', 'Joined'],
    members.map((m) => {
      const active = m.acceptedAt && !m.deactivatedAt;
      const status = m.deactivatedAt
        ? ['pill pill-danger', 'Deactivated']
        : m.acceptedAt
          ? ['pill pill-ok', 'Active']
          : ['pill pill-warn', 'Invited'];
      return el('tr', {}, [
        el('td', {}, [
          el('div', { class: 'cell-strong', textContent: m.user?.name || m.user?.email || '—' }),
          m.user?.name && m.user?.email
            ? el('div', { class: 'cell-muted', textContent: m.user.email })
            : null,
        ]),
        el('td', {}, [el('span', { class: 'pill', textContent: m.role })]),
        el('td', { class: 'cell-muted', textContent: m.venue?.name ?? '—' }),
        el('td', {}, [el('span', { class: status[0], textContent: status[1] })]),
        el('td', {
          class: 'cell-muted',
          textContent: fmtDate(active ? m.acceptedAt : m.invitedAt),
        }),
      ]);
    }),
  );
}

function devicesTable(devices: DeviceRow[]): HTMLElement {
  return table(
    ['Device', 'Platform', 'Attribution', 'Venue', 'Last seen', 'Status'],
    devices.map((d) =>
      el('tr', {}, [
        el('td', { class: 'cell-strong', textContent: d.deviceName }),
        el('td', { class: 'cell-muted', textContent: d.platform }),
        el('td', { class: 'cell-muted', textContent: d.attributionMode }),
        el('td', { class: 'cell-muted', textContent: d.venue?.name ?? '—' }),
        el('td', { class: 'cell-muted', textContent: relativeTime(d.lastSeenAt) }),
        el('td', {}, [
          el('span', {
            class: d.revokedAt ? 'pill pill-danger' : 'pill pill-ok',
            textContent: d.revokedAt ? 'Revoked' : 'Active',
          }),
        ]),
      ]),
    ),
  );
}

function auditTable(rows: AuditRow[]): HTMLElement {
  return table(
    ['When', 'Actor', 'Action', 'Target'],
    rows.map((a) =>
      el('tr', {}, [
        el('td', { class: 'cell-muted', textContent: fmtDate(a.timestamp) }),
        el('td', {
          class: 'cell-muted',
          textContent: a.actorUser?.name || a.actorUser?.email || 'system',
        }),
        el('td', {}, [el('span', { class: 'pill', textContent: a.action })]),
        el('td', { class: 'cell-muted', textContent: `${a.targetEntity} ${a.targetId}` }),
      ]),
    ),
  );
}
